import { Database } from "./supabase";

export type Organization = Database["public"]["Tables"]["organizations"]["Row"];
export type OrganizationInsert = Database["public"]["Tables"]["organizations"]["Insert"];
export type OrganizationUpdate = Database["public"]["Tables"]["organizations"]["Update"];

export type OrganizationMember = Database["public"]["Tables"]["organization_members"]["Row"];
export type OrganizationMemberInsert =
  Database["public"]["Tables"]["organization_members"]["Insert"];
export type OrganizationMemberUpdate =
  Database["public"]["Tables"]["organization_members"]["Update"];

export type OrganizationMemberMetadata = {
  selectedRoles?: string[];
  customRole?: string;
  selectedGoal?: string;
  customGoal?: string;
  selectedSize?: string;
  invites?: string[];
  invited?: boolean;
  email?: string;
  language?: string;
};

export type OrganizationWithMembers = Organization & {
  organization_members: OrganizationMember[];
};

export type Profile = Database["public"]["Tables"]["profiles"]["Row"];
export type ProfileInsert = Database["public"]["Tables"]["profiles"]["Insert"];
export type ProfileUpdate = Database["public"]["Tables"]["profiles"]["Update"];

export type Project = Database["public"]["Tables"]["projects"]["Row"] & {
  estimated_completion_time?: string | null;
  subcontractor_suggestions?: string[];
  missing_codes?: {
    code: string;
    definition?: string; // Optional - fetched from database when needed
    justification: string;
    confidence_level?: number;
  }[];
  chat_backend_type?: "python" | "typescript"; // Chat backend selection for project-level chat
  // Extended to support project_context until migration is deployed and types regenerated
  project_context?: {
    phase: string | null;
    focus_area: string;
    notes: string;
  } | null;
};
export type ProjectInsert = Database["public"]["Tables"]["projects"]["Insert"] & {
  // Extended to support project_context until migration is deployed and types regenerated
  project_context?: {
    phase: string;
    focus_area: string;
    notes: string;
  };
};
export type ProjectUpdate = Database["public"]["Tables"]["projects"]["Update"] & {
  // Extended to support project_context until migration is deployed and types regenerated
  project_context?: {
    phase: string | null;
    focus_area: string;
    notes: string;
  };
};

// Project Memory Notes types - using generated types
export type ProjectMemoryNote = Database["public"]["Tables"]["project_memory_notes"]["Row"];
export type ProjectMemoryNoteInsert =
  Database["public"]["Tables"]["project_memory_notes"]["Insert"];
export type ProjectMemoryNoteUpdate =
  Database["public"]["Tables"]["project_memory_notes"]["Update"];

// Extended types with additional constraints
export type ProjectMemoryNoteWithSourceType = ProjectMemoryNote & {
  author_source_type: "human" | "ai";
};

export type ProjectMemoryNoteInsertWithSourceType = ProjectMemoryNoteInsert & {
  author_source_type?: "human" | "ai";
};

// UI-specific project memory type for the PlanningSection component
export type ProjectMemoryUI = {
  id: string;
  text: string;
  addedBy: string;
  addedByName: string;
  isProjectLevel: boolean;
  topic?: string;
  tags?: string[];
  created_at?: string;
  updated_at?: string;
};

export type IndexingStatus = Database["public"]["Enums"]["indexing_status_enum"];
export type ProjectAnalysisStatus = Database["public"]["Enums"]["project_analysis_status"];

export type WaitlistEntry = Database["public"]["Tables"]["waitlist"]["Row"];
export type WaitlistInsert = Database["public"]["Tables"]["waitlist"]["Insert"];
export type WaitlistUpdate = Database["public"]["Tables"]["waitlist"]["Update"];

// Extended Conversation types to support both project and organization conversations
export type Conversation = Database["public"]["Tables"]["conversations"]["Row"] & {
  organization_id?: string | null;
};

export type ConversationInsert = Database["public"]["Tables"]["conversations"]["Insert"] & {
  organization_id?: string | null;
  project_id?: string | null; // Make project_id optional since conversations can be org-level too
};

export type ConversationUpdate = Database["public"]["Tables"]["conversations"]["Update"];

// Message types
export type Message = Database["public"]["Tables"]["messages"]["Row"];
export type MessageInsert = Database["public"]["Tables"]["messages"]["Insert"];
export type MessageUpdate = Database["public"]["Tables"]["messages"]["Update"];

// Message role type for better type safety
export type MessageRole = "user" | "assistant" | "system";

// ================================================================
// CHECKLIST SYSTEM TYPES
// ================================================================

// Organization Default Checklist Types - using generated types
export type OrganizationDefaultChecklist =
  Database["public"]["Tables"]["organization_default_checklists"]["Row"];
export type OrganizationDefaultChecklistInsert =
  Database["public"]["Tables"]["organization_default_checklists"]["Insert"];
export type OrganizationDefaultChecklistUpdate =
  Database["public"]["Tables"]["organization_default_checklists"]["Update"];

// Organization Default TODO Types - using generated types
export type OrganizationDefaultTodo =
  Database["public"]["Tables"]["organization_default_todos"]["Row"];
export type OrganizationDefaultTodoInsert =
  Database["public"]["Tables"]["organization_default_todos"]["Insert"];
export type OrganizationDefaultTodoUpdate =
  Database["public"]["Tables"]["organization_default_todos"]["Update"];

// Project Checklist Types - using generated types
export type ProjectChecklist = Database["public"]["Tables"]["project_checklists"]["Row"];
export type ProjectChecklistInsert = Database["public"]["Tables"]["project_checklists"]["Insert"];
export type ProjectChecklistUpdate = Database["public"]["Tables"]["project_checklists"]["Update"];

// Project TODO Types - using generated types
export type ProjectTodo = Database["public"]["Tables"]["project_todos"]["Row"];
export type ProjectTodoInsert = Database["public"]["Tables"]["project_todos"]["Insert"];
export type ProjectTodoUpdate = Database["public"]["Tables"]["project_todos"]["Update"];

// Extended types with relationships
export interface OrganizationDefaultChecklistWithTodos extends OrganizationDefaultChecklist {
  organization_default_todos: OrganizationDefaultTodo[];
}

export interface ProjectChecklistWithTodos extends ProjectChecklist {
  project_todos: ProjectTodo[];
}

export interface ProjectTodoWithAssignee extends ProjectTodo {
  assigned_user?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string;
  } | null;
}

export interface ProjectChecklistWithTodosAndAssignees extends ProjectChecklist {
  project_todos: ProjectTodoWithAssignee[];
}

// Checklist progress summary type
export interface ChecklistProgress {
  checklist_id: string;
  name: string;
  completed_tasks: number;
  total_tasks: number;
  progress_percentage: number;
}

// Project checklist overview type
export interface ProjectChecklistOverview {
  project_id: string;
  checklists: ChecklistProgress[];
  overall_completed: number;
  overall_total: number;
  overall_progress_percentage: number;
}

// ================================================================
// END CHECKLIST SYSTEM TYPES
// ================================================================

// ================================================================
// SUPPLIER SYSTEM TYPES
// ================================================================

// Basic supplier types using generated types
export type Supplier = Database["public"]["Tables"]["suppliers"]["Row"];
export type SupplierInsert = Database["public"]["Tables"]["suppliers"]["Insert"];
export type SupplierUpdate = Database["public"]["Tables"]["suppliers"]["Update"];

export type ProjectSupplier = Database["public"]["Tables"]["project_suppliers"]["Row"];
export type ProjectSupplierInsert = Database["public"]["Tables"]["project_suppliers"]["Insert"];
export type ProjectSupplierUpdate = Database["public"]["Tables"]["project_suppliers"]["Update"];

export type SupplierAmaCode = Database["public"]["Tables"]["supplier_ama_codes"]["Row"];
export type SupplierAmaCodeInsert = Database["public"]["Tables"]["supplier_ama_codes"]["Insert"];
export type SupplierAmaCodeUpdate = Database["public"]["Tables"]["supplier_ama_codes"]["Update"];

export type SupplierTag = Database["public"]["Tables"]["supplier_tags"]["Row"];
export type SupplierTagInsert = Database["public"]["Tables"]["supplier_tags"]["Insert"];
export type SupplierTagUpdate = Database["public"]["Tables"]["supplier_tags"]["Update"];

// Supplier status enum
export type SupplierStatus = Database["public"]["Enums"]["supplier_status"];

// Extended types with relationships
export interface SupplierWithAmaCodes extends Supplier {
  supplier_ama_codes: SupplierAmaCode[];
}

export interface SupplierWithTags extends Supplier {
  supplier_tags: SupplierTag[];
}

export interface SupplierWithAmaCodesAndTags extends Supplier {
  supplier_ama_codes: SupplierAmaCode[];
  supplier_tags: SupplierTag[];
}

export interface ProjectSupplierWithSupplier extends ProjectSupplier {
  supplier: Supplier;
}

export interface ProjectSupplierWithSupplierAndAmaCodes extends ProjectSupplier {
  supplier: SupplierWithAmaCodes;
}

// Supplier recommendation types for AI analysis
export interface SupplierRecommendation {
  tag: string;
  supplier: {
    id: string;
    name: string;
    contact_email?: string | null;
    contact_phone?: string | null;
    contact_person_name?: string | null;
    description?: string | null;
  } | null;
  reason?: string;
  confidence: number;
  note?: string;
}

// Supplier matching types for AMA code analysis
export interface SupplierMatch {
  supplier: Supplier;
  matched_ama_codes: string[];
  confidence_score: number;
  is_already_associated: boolean;
}

// Missing code item type for project analysis
export interface MissingCodeItem {
  code: string;
  definition?: string; // Optional - fetched from database when needed
  justification: string;
  confidence_level?: number;
}

// ================================================================
// END SUPPLIER SYSTEM TYPES
// ================================================================

// SharePoint integration types
export interface SharePointPath {
  path: string;
  siteId: string;
  driveId: string;
  listId?: string;
}

export interface SharePointConfig {
  paths: SharePointPath[];
  connectedUserId: string;
  lastSyncAt?: string;
  isEnabled: boolean;
}

export interface SharePointOrgSelectedConnection {
  organization_id: string;
  status: "disconnected" | "needs_runtime_consent" | "needs_setup_login" | "ready" | "error";
  home_tenant_id?: string | null;
  runtime_admin_consent_at?: string | null;
  setup_expires_at?: string | null;
  last_verified_at?: string | null;
  last_sync_at?: string | null;
  last_error?: string | null;
}

export interface SharePointOrgApprovedSite {
  id: string;
  organization_id: string;
  site_id: string;
  site_name?: string | null;
  display_name?: string | null;
  web_url?: string | null;
  permission_role: "read" | "write";
  status: "active" | "revoked" | "error";
  approved_at: string;
  last_verified_at?: string | null;
  last_error?: string | null;
}

export interface SharePointOrgDrive {
  id: string;
  organization_id: string;
  site_id: string;
  drive_id: string;
  drive_name?: string | null;
  web_url?: string | null;
  list_id?: string | null;
  delta_link?: string | null;
  sync_enabled: boolean;
  poll_status: "active" | "paused" | "error";
  hot_window_until?: string | null;
  last_polled_at?: string | null;
  last_success_at?: string | null;
  next_poll_at?: string | null;
  last_error?: string | null;
}

export interface SharePointOrgSyncRoot {
  id: string;
  organization_id: string;
  site_id: string;
  drive_id: string;
  list_id?: string | null;
  root_path: string;
  root_name?: string | null;
  root_item_id?: string | null;
  is_enabled: boolean;
  sharepoint_org_drives?: {
    drive_name?: string | null;
    web_url?: string | null;
    sync_enabled?: boolean | null;
    poll_status?: "active" | "paused" | "error" | null;
  } | null;
}

export interface SharePointOrgSelectedStatus {
  connection: SharePointOrgSelectedConnection;
  approvedSites: SharePointOrgApprovedSite[];
  drives: SharePointOrgDrive[];
  syncRoots: SharePointOrgSyncRoot[];
  runtimeConsentGranted: boolean;
  setupCompletedOnce: boolean;
  hasBrowsableRoots: boolean;
}

// OneDrive integration types
export interface OneDrivePath {
  path: string;
  driveId: string; // For OneDrive, this is the user's OneDrive drive ID
}

export interface OneDriveConfig {
  paths: OneDrivePath[];
  connectedUserId: string;
  lastSyncAt?: string;
  isEnabled: boolean;
}

// Combined Microsoft integration config
export interface MicrosoftIntegrationConfig {
  sharepoint?: SharePointConfig;
  onedrive?: OneDriveConfig;
}

export type OrganizationWithSharePoint = Organization & {
  sharepoint_config?: SharePointConfig | null;
};

// Project member types
export type ProjectMemberRow = Database["public"]["Tables"]["project_members"]["Row"];
export type ProjectMemberInsert = Database["public"]["Tables"]["project_members"]["Insert"];
export type ProjectMemberUpdate = Database["public"]["Tables"]["project_members"]["Update"];

export type ProjectMemberRole = "ADMIN" | "EDITOR" | "VIEWER";

// Phase-related types
export type PhaseStatus = Database["public"]["Enums"]["phase_status"];

export type OrganizationDefaultPhase =
  Database["public"]["Tables"]["organization_default_phases"]["Row"];
export type OrganizationDefaultPhaseInsert =
  Database["public"]["Tables"]["organization_default_phases"]["Insert"];
export type OrganizationDefaultPhaseUpdate =
  Database["public"]["Tables"]["organization_default_phases"]["Update"];

export type ProjectPhase = Database["public"]["Tables"]["project_phases"]["Row"];
export type ProjectPhaseInsert = Database["public"]["Tables"]["project_phases"]["Insert"];
export type ProjectPhaseUpdate = Database["public"]["Tables"]["project_phases"]["Update"];

// Extended project type with phases
export type ProjectWithPhases = Project & {
  phases?: ProjectPhase[];
};

// ================================================================
// FEEDBACK SYSTEM TYPES
// ================================================================

// Feedback context type discriminator
export type FeedbackContextType = "chat_message";

// Feedback rating (thumbs up/down)
export type FeedbackRating = "positive" | "negative";

// Feedback conversation type (mirrors convo_type enum)
export type FeedbackConvoType = "project" | "org";

// Main Feedback type
export interface Feedback {
  id: string;
  context_type: FeedbackContextType;
  organization_id: string;
  project_id: string;
  message_id: string | null;
  conversation_id: string | null;
  convo_type: FeedbackConvoType | null;
  // Feedback content
  rating: FeedbackRating;
  comment: string | null;
  // User tracking
  user_id: string;
  // Timestamps
  created_at: string;
  updated_at: string;
}

// Insert type - omits auto-generated fields
export type FeedbackInsert = Omit<Feedback, "id" | "created_at" | "updated_at">;

// Update type - only rating and comment can be updated
export type FeedbackUpdate = Partial<Pick<Feedback, "rating" | "comment">>;

// Chat feedback specific insert type
export interface ChatFeedbackInsert {
  organization_id: string;
  project_id: string;
  message_id: string;
  conversation_id: string;
  convo_type: FeedbackConvoType;
  rating: FeedbackRating;
  comment?: string | null;
}

// Feedback stats for aggregated view
export interface FeedbackStats {
  positive: number;
  negative: number;
  total: number;
}

// ================================================================
// END FEEDBACK SYSTEM TYPES
// ================================================================

// ================================================================
// CANVAS DOCUMENT TYPES
// ================================================================

// Basic canvas document types using generated types
export type CanvasDocument = Database["public"]["Tables"]["canvas_documents"]["Row"];
export type CanvasDocumentInsert = Database["public"]["Tables"]["canvas_documents"]["Insert"];
export type CanvasDocumentUpdate = Database["public"]["Tables"]["canvas_documents"]["Update"];

export type CanvasDocumentVersion = Database["public"]["Tables"]["canvas_document_versions"]["Row"];
export type CanvasDocumentVersionInsert =
  Database["public"]["Tables"]["canvas_document_versions"]["Insert"];

// Report type enum
export type CanvasReportType = "main" | "draft" | "archived";

// Document export types (extended until migration deployed)
export type DocumentExport = {
  id: string;
  document_id: string;
  version_id: string | null;
  project_id: string;
  file_path: string;
  file_name: string;
  file_size_bytes: number;
  exported_by: string | null;
  created_at: string;
};

// ================================================================
// END CANVAS DOCUMENT TYPES
// ================================================================

// ================================================================
// PROJECT CONTEXT ENUMS
// ================================================================

/**
 * Project phase enum.
 * Values must match database CHECK constraint in project_context JSONB.
 */
export enum ProjectPhaseEnum {
  Active = "active",
  Historical = "historical",
}

/**
 * Focus area enum for construction projects.
 * Values must match database CHECK constraint in project_context JSONB.
 */
export enum FocusAreaEnum {
  GeneralContractor = "general_contractor",
  Electrical = "electrical",
  Hvac = "hvac",
  Civil = "civil",
  Infrastructure = "infrastructure",
  Traffic = "traffic",
  Installations = "installations",
  PropertyDeveloper = "property_developer",
}

// ================================================================
// END PROJECT CONTEXT ENUMS
// ================================================================
